/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/anchor-is-valid */
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { window } from '../../../../globals';
import { track } from '../../../../helpers/analytics';
import useOnMount from '../../../../hooks/useOnMount';
import usePrevious from '../../../../hooks/usePrevious';
import ProjectsModule from '../../../../redux/projects';
import { nuc } from '../../../../utils';
import CheckmarkCircleLoader from '../../../generic/CheckmarkCircleLoader';
import CopyPreformattedToClipboard from '../../../generic/CopyPreformattedToClipboard';
import { icons, IconText } from '../../../generic/Icon';
import DocsLink from '../../../generic/Link/DocsLink';
import Link from '../../../generic/Link/Link';
import Text from '../../../generic/Text';
import type { Project } from '../../../../types';

import APITokenStep from './APITokenStep';

const PROJECT_REDIRECT_TIMEOUT = 2 * 1000; // 2s

type CLIUploadGuideContainerProps = {
  router: InjectedRouter;
};

/**
 * Container component for the CLIUploadGuide.
 * Manages the state of the platform tabs, as
 * well as setting the polling for recently added projects.
 */
const CLIUploadGuideContainer: VoidFunctionComponent<CLIUploadGuideContainerProps> = ({
  router,
}) => {
  const dispatch = useDispatch();
  //  Manage the state of the tabs, which alter the install instructions which are displayed
  const [platform, setPlatform] = useState<CLIUploadGuideProps['platform']>('nix');

  const onNixClick = useCallback(() => {
    if (platform !== 'nix') {
      setPlatform('nix');
    }
  }, [platform, setPlatform]);
  const onDosClick = useCallback(() => {
    if (platform !== 'dos') {
      setPlatform('dos');
    }
  }, [platform, setPlatform]);

  //  Manage displaying the recently updated project, and navigating the user off to that project.
  const projects: Project[] = useSelector(ProjectsModule.selectors.getProjectList);
  const [newProject, setNewProject] = useState<Project | null>(null);

  const prevProjects = usePrevious(projects);

  //  Poll for projects on mount, and remove the polling when the component unmounts
  useOnMount(
    function onMount() {
      dispatch(ProjectsModule.actions.startPollProjects(window.user?.organizationId as number));
    },
    function onUnmount() {
      dispatch(ProjectsModule.actions.stopPollProjects());
    },
  );

  //  Set the `newProject` when new projects are detected.
  useEffect(() => {
    //  Because we are polling for projects (and the reference to projects changes every time),
    //  we need to guard against setting the new project when nothing is actually new.
    //  We only want to set the "new project" when:
    //  - it hasn't previously been set
    //  - there are no projects in the list (if they aren't fetched yet, or if there are just 0)
    //  - if prevProjects is empty (which happens on the initial render, as it begins as empty).
    //  - if projects has more projects than prevProjects
    if (
      newProject === null &&
      projects.length > 0 &&
      (prevProjects?.length ?? 0) > 0 &&
      projects.length > (prevProjects?.length ?? 0)
    ) {
      const lastUploadedProject = _.last(projects.filter(project => project.require_mediated_dependencies));
      if (lastUploadedProject) {
        setNewProject(lastUploadedProject);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We intentionally leave prevProjects out here.
  }, [projects]);

  useEffect(() => {
    if (newProject) {
      //  Track via Heap
      track('CLI Upload', { locator: newProject.locator as string });
      //  After the timeout, redirect the user to the project summary page.
      setTimeout(
        () => router.push(`/projects/${nuc(newProject.locator as string)}`),
        PROJECT_REDIRECT_TIMEOUT,
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- router is stable.
  }, [newProject]);

  return (
    <CLIUploadGuide
      newProject={newProject}
      platform={platform}
      onNixClick={onNixClick}
      onDosClick={onDosClick}
    />
  );
};

type UploadSteps = {
  DOWNLOAD: string;
  API_KEY: string;
  ANALYZE: string;
};

export const UPLOAD_STEPS: Record<CLIUploadGuideProps['platform'], UploadSteps> = {
  nix: {
    DOWNLOAD: `curl -H 'Cache-Control: no-cache' https://raw.githubusercontent.com/fossas/fossa-cli/master/install-latest.sh | bash`,
    API_KEY: `export FOSSA_API_KEY=`,
    ANALYZE: `fossa analyze`,
  },
  dos: {
    DOWNLOAD: `Set-ExecutionPolicy Bypass -Scope Process -Force; iex  ((New-Object System.Net.WebClient).DownloadString('https://raw.githubusercontent.com/fossas/fossa-cli/master/install-latest.ps1'))`,
    API_KEY: `$env:FOSSA_API_KEY=`,
    ANALYZE: `fossa analyze`,
  },
};

const CLOUD_HOST = 'app.fossa.com';

/**
 * If the host domain we are on is NOT `app.fossa.com`,
 * we include the endpoint arg to help avoid trying the wrong one.
 */
const getEndpointArg = (): string => {
  const { host, origin } = window.location;
  if (host === CLOUD_HOST) {
    return '';
  }
  return ` -e ${origin}`;
};

export type CLIUploadGuidePlatform = 'nix' | 'dos';

type CLIUploadGuideProps = {
  newProject: Project | null;
  platform: CLIUploadGuidePlatform;
  onNixClick: () => void;
  onDosClick: () => void;
};

/**
 * UI Component which will display CLI upload instructions,
 * depending on the users platform (nix or dos), as the
 * instructions are slightly different. It also
 * displays the project info once the project is uploaded.
 */
const CLIUploadGuide: VoidFunctionComponent<CLIUploadGuideProps> = ({
  newProject,
  platform,
  onNixClick,
  onDosClick,
}) => (
  <div className="panel">
    <div className="panel-heading-light panel-heading-warning">
      <IconText
        icon={icons.warning}
        text="This step requires a technical user"
      />
    </div>
    <ul className="nav nav-tabs nav-tabs-min">
      <li className={platform === 'nix' ? 'active' : ''}>
        <a onClick={onNixClick}>
          <IconText
            icon={icons.linux}
            text="Mac / Linux"
          />
        </a>
      </li>
      <li className={platform === 'dos' ? 'active' : ''}>
        <a onClick={onDosClick}>
          <IconText
            icon={icons.windows}
            text="Windows"
          />
        </a>
      </li>
    </ul>
    <div className="padded padded-small">
      <Text.H4>
        1. Install
        {' '}
        <Link
          to="https://github.com/fossas/fossa-cli"
          openInNewTab
        >
          `fossa-cli`
        </Link>
      </Text.H4>
      <Text.Paragraph>
        Run the following command from a local development Terminal or CI machine:
      </Text.Paragraph>
    </div>
    <CopyPreformattedToClipboard text={UPLOAD_STEPS[platform].DOWNLOAD} />
    <APITokenStep platform={platform} />
    <div className="padded padded-small">
      <Text.H4>
        3. Run an analysis
      </Text.H4>
      <Text.Paragraph>
        Navigate into your code directory and run the following command.
      </Text.Paragraph>
    </div>
    <CopyPreformattedToClipboard text={`${UPLOAD_STEPS[platform].ANALYZE}${getEndpointArg()}`} />
    <div className="padded padded-small">
      <Text.H4>
        4. View your new project
      </Text.H4>
      <Text.Paragraph>
        We&apos;ll automatically take you to your new project once your upload is detected.
      </Text.Paragraph>
      <div className="text-center padding-top-lg padding-bottom-lg">
        <CheckmarkCircleLoader complete={!!newProject} />
        {newProject ? (
          <div>
            <Text.H3>
              {newProject.title}
            </Text.H3>
            <Text.H4>
              Upload successful! Redirecting...
            </Text.H4>
          </div>
        ) : (
          <Text.H4>
            Waiting for you to upload your project...
          </Text.H4>
        )}
      </div>
    </div>
    <div className="padded padded-small text-center padding-bottom-xs">
      <Text.H4>
        <IconText
          icon={icons.questionCircle}
          text="Need Help?"
        />
      </Text.H4>
      <Text.Paragraph>
        Troubleshoot by viewing
        {' '}
        <DocsLink to="importing-a-project">
          <u>our docs</u>
        </DocsLink>
        .
      </Text.Paragraph>
    </div>
  </div>
);

export default withRouter(CLIUploadGuideContainer);
